interface MIConfig {
	auth: {
		auth0Aud: string;
		auth0ClientId: string;
		auth0Domain: string;
		auth0ForwardParams?: string;
		auth0CookieDomain?: string;

		// Better Auth configuration
		// defaults to using api host without the /api path
		baseUrl?: string;
		// Auth method determines which better auth plugin to use
		// if not provided, we use our own signin page
		method?: 'sso';
		// Provider ID determines which better auth plugin to use
		providerId?: 'saml';
		// SAML IdP logout URL (e.g., https://your-tenant.us.auth0.com/v2/logout)
		// only used for SAML SSO when protocol is 'saml'
		samlIdpLogoutUrl?: string;
	};
	microsoftPlaces: {
		clientId: string;
		api: string;
	};
	hosts: {
		web: string;
		api: string;
		viewer: string;
		viewerEmbed: string;
		contact: string;
		pricing: string;
		privacyPolicy: string;
		termsAndConditions: string;
		termsAndConditionsSubscriber: string;
		termsAndConditionsRegister: string;
		iosHost: string;
		msPlacesGuideHost: string;
		msPlacesIntegrationGuideHost: string;
		viewerGuide: string;
	};
	appName: string;
	sentryEnabled: string | boolean;
	sentryUrl: string;
	ffProvider: 'posthog' | 'api';
	posthogKey: string;
	posthogHost: string;
	userflowToken: string;
	intercomAppId: string;
	cdnHost: string;
	outdoorURL: string;
	outdoorKey: string;
	streetTilesProvider: string;
	azureTiles: {
		host: string;
		version: string;
		satelliteTilesetId: string;
	};
	version: string;
	emailEnabled: boolean;
	inviteOnlyMode: boolean;
	hubspotPortalId?: string;
	featureFlags: Record<string, string>;
}

declare global {
	namespace NodeJS {
		interface Global {
			BUILD_NUMBER: string;
			MI: MIConfig;
		}
	}
}

globalThis.BUILD_NUMBER = import.meta.env.BUILD_NUMBER || '0';

function getHost(
	envVar: string | null | undefined,
	defaultValue: string
): string {
	if (envVar == null) {
		return defaultValue;
	}
	try {
		return new URL(envVar).origin;
	} catch {
		return defaultValue;
	}
}

/**
 * Extracts config values from process in dev and from the environment.js file in production.
 * Note: Vite will only include variables that are prefixed with CLIENT_ in the built application.
 * Please add any updates to `./server/entrypoint.sh`, `./.env.example`, and here.
 */
globalThis.MI =
	globalThis.MI ||
	({
		auth: {
			auth0Aud: import.meta.env.CLIENT_AUTH0_AUD ?? '',
			auth0ClientId: import.meta.env.CLIENT_AUTH0_CLIENT_ID ?? '',
			auth0Domain: import.meta.env.CLIENT_AUTH0_DOMAIN ?? '',
			auth0ForwardParams: import.meta.env.CLIENT_AUTH0_FORWARD_PARAMS,
			auth0CookieDomain: import.meta.env.CLIENT_AUTH0_COOKIE_DOMAIN,

			// Better Auth configuration
			baseUrl: import.meta.env.CLIENT_AUTH_BASE_URL,
			// Auth method: 'sso' for SSO (default), 'oauth2' for Auth0 OAuth
			method: import.meta.env.CLIENT_AUTH_METHOD as 'sso' | undefined,
			// Provider ID: 'saml' for SAML SSO (default), 'oauth2' for Auth0 OAuth
			providerId: import.meta.env.CLIENT_AUTH_PROVIDER_ID as 'saml' | undefined,
			// SAML IdP logout URL (e.g., https://your-tenant.us.auth0.com/v2/logout)
			samlIdpLogoutUrl: import.meta.env.CLIENT_AUTH_SAML_IDP_LOGOUT_URL,
		},
		microsoftPlaces: {
			clientId:
				import.meta.env.CLIENT_PLACES_APP_MICROSOFT_CLIENT_ID ??
				'8ab9d791-039f-432a-9523-34cb19c9f477',
			api:
				import.meta.env.CLIENT_PLACES_APP_MICROSOFT_GRAPH_API ??
				'https://graph.microsoft.com/v1.0',
		},
		hosts: {
			web: (import.meta.env.CLIENT_APP_HOST ?? '') + import.meta.env.BASE_URL,
			api:
				getHost(
					import.meta.env.CLIENT_APP_HOST,
					globalThis.location?.origin ?? ''
				).replace('http://', 'https://') + '/api',
			viewer: import.meta.env.CLIENT_VIEWER_HOST ?? '',
			viewerEmbed: import.meta.env.CLIENT_VIEWER_EMBED_HOST ?? '',
			contact:
				import.meta.env.CLIENT_CONTACT_HOST ??
				'https://www.mappedin.com/contact-us/',
			pricing:
				import.meta.env.CLIENT_PRICING_HOST ??
				'https://www.mappedin.com/pricing',
			privacyPolicy:
				import.meta.env.CLIENT_PRIVACY_POLICY_URL ??
				'https://info.mappedin.com/privacy-policy',
			termsAndConditions:
				import.meta.env.CLIENT_TERMS_AND_CONDITIONS_URL ??
				'https://info.mappedin.com/terms',
			termsAndConditionsSubscriber:
				import.meta.env.CLIENT_TERMS_AND_CONDITIONS_SUBSCRIBER_HOST ??
				'https://info.mappedin.com/terms',
			termsAndConditionsRegister:
				import.meta.env.CLIENT_TERMS_AND_CONDITIONS_REGISTER_HOST ??
				'https://info.mappedin.com/terms',
			iosHost:
				import.meta.env.CLIENT_IOS_HOST ??
				'itms-apps://apps.apple.com/ca/app/mappedin-maker/id6449945802',
			msPlacesGuideHost:
				import.meta.env.CLIENT_MS_PLACES_GUIDE_HOST ??
				'https://developer.mappedin.com/docs/enterprise-apps/microsoft-places-configuration/',
			msPlacesIntegrationGuideHost: import.meta.env
				.CLIENT_MS_PLACES_INTEGRATION_GUIDE_HOST,
			viewerGuide:
				import.meta.env.CLIENT_VIEWER_GUIDE_HOST ??
				'https://developer.mappedin.com/docs/category/embed-a-map',
		},
		appName: 'Mappedin Maker',
		sentryEnabled: import.meta.env.CLIENT_SENTRY_ENABLED ?? 'false',
		sentryUrl: import.meta.env.CLIENT_SENTRY_URL ?? '',
		ffProvider: (import.meta.env.CLIENT_FF_PROVIDER ?? 'posthog') as
			| 'posthog'
			| 'api',
		posthogKey: import.meta.env.CLIENT_POSTHOG_KEY ?? '',
		userflowToken: import.meta.env.CLIENT_USERFLOW_TOKEN ?? '',
		intercomAppId: import.meta.env.CLIENT_INTERCOM_APP_ID ?? '',
		posthogHost: import.meta.env.CLIENT_POSTHOG_HOST ?? '',
		cdnHost: '',
		outdoorURL: import.meta.env.CLIENT_OUTDOOR_URL ?? '',
		outdoorKey: import.meta.env.CLIENT_OUTDOOR_KEY ?? '',
		streetTilesProvider: import.meta.env.CLIENT_STREET_TILES_PROVIDER ?? '',
		azureTiles: {
			host: import.meta.env.CLIENT_AZURE_TILES_HOST ?? 'atlas.microsoft.com',
			version: import.meta.env.CLIENT_AZURE_TILES_VERSION ?? '2024-04-01',
			satelliteTilesetId:
				import.meta.env.CLIENT_AZURE_TILES_SATELLITE_TILESET_ID ??
				'microsoft.imagery',
		},
		version: import.meta.env.VERSION ?? '0',
		emailEnabled: import.meta.env.CLIENT_EMAIL_ENABLED === 'true',
		inviteOnlyMode: import.meta.env.CLIENT_INVITE_ONLY_MODE === 'true',
		hubspotPortalId: import.meta.env.CLIENT_HUBSPOT_PORTAL_ID,
		featureFlags: {},
	} satisfies MIConfig);

export default globalThis.MI as MIConfig;
